import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IInitialState, RootState } from '../../../../reduxService';
import { IProjectResultResponse } from '../../api/IApiResponse';
import * as BeMaccaferriCalculationHelper from '../../api/calculation/BeMaccaferriCalculationHelper';
import { hasConcreteMasonry, validateProjectBeforeRun } from '../../utils/utils';
import { IProjectMacra1 } from './projectSlice';
import { warning } from '../../../../components/Alert/Alert';
import i18n from '../../../localization';
import { roundToTwo } from '../../components/InputForm/Geometry/utils';
import { UnitOfMeasure } from '../../../enum/project';
import { EConversionSIToImperial, convertTo } from '../../../conversion';

const initialState: IInitialState<IProjectResultResponse> = {
    initialState: true,
};

export const resultsSlice = createSlice({
    name: 'resultsMacra1',
    initialState,
    reducers: {
        cleanResults: state => {
            state.initialState = true;
            state.data = undefined;
        },
        setDesignImg: (state, action: PayloadAction<string>) => {
            if (!state.initialState) state.data!.channelDesignImg = action.payload;
        },
        setDischargeCurveImg: (state, action: PayloadAction<string>) => {
            if (!state.initialState) state.data!.dischargeCurveImg = action.payload;
        },
        setDischargeAlert: (state, action: PayloadAction<boolean>) => {
            state.showDischargeAlert = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchProjectResults.pending, state => {
                state.data = undefined;
                state.pending = true;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchProjectResults.fulfilled, (state, action) => {
                state.data = action.payload;
                state.pending = false;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchProjectResults.rejected, (state, action) => {
                state.data = undefined;
                state.pending = false;
                state.error = {
                    status: (action.payload as any).status,
                    data: (action.payload as any).data,
                };
                state.initialState = false;
            });
    },
});

export const fetchProjectResults = createAsyncThunk('api/Calculation/GetResultsMacra1', async (_undefined, { rejectWithValue, getState }) => {
    const state: RootState = getState() as RootState;
    const projectInfo = state.projectInfo;
    const showDischargeAlert = state.macra1.results.showDischargeAlert;
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    try {
        if (validateProjectBeforeRun(state.macra1.project.data!)) {
            if (hasConcreteMasonry(state.macra1.project.data!)) {
                warning('', `${i18n.t('material_concrete_masonry_warning')}`);
            }
            const results = await BeMaccaferriCalculationHelper.getResultsAsync(state.macra1.project.data!);
            if (showDischargeAlert)
                warning(
                    i18n.t('hydraulic_data_manual_discharge_max_value_warning', {
                        max_value: isImperial
                            ? roundToTwo(convertTo(results.data.generalResult.general.discharge, EConversionSIToImperial.m3s_to_ft3s))
                            : roundToTwo(results.data.generalResult.general.discharge),
                    }),
                );
            return results.data;
        }
    } catch (error: any) {
        if (showDischargeAlert) warning(i18n.t('hydraulic_data_manual_discharge_max_value_warning'));
        return rejectWithValue(error.response);
    }
});

export const { cleanResults, setDesignImg, setDischargeCurveImg, setDischargeAlert } = resultsSlice.actions;

export default resultsSlice.reducer;
